/* eslint-disable no-console */

/**
 * Helper do obsługi przekierowań na subdomeny językowe
 */

/**
 * Sprawdza czy host jest lokalnym hostem lub adresem IP
 * @param {string} hostNoPort - host bez portu
 * @returns {boolean}
 */
const isLocalEnvironment = (hostNoPort) => {
	const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(hostNoPort);
	const isLocalhost =
		hostNoPort === "localhost" || hostNoPort.endsWith(".localhost");

	return isIp || isLocalhost;
};

/**
 * Parsuje informacje o hoście z window.location
 * @returns {Object} - obiekt z informacjami o hoście
 */
const parseHostInfo = () => {
	if (typeof window === "undefined") {
		return null;
	}

	const { location } = window;
	const host = location.host; // może zawierać port
	const hostNoPort = host.split(":")[0];
	const parts = hostNoPort.split(".");
	const port = host.includes(":") ? `:${host.split(":")[1]}` : "";

	return {
		location,
		host,
		hostNoPort,
		parts,
		port,
		isLocal: isLocalEnvironment(hostNoPort),
	};
};

/**
 * Pobiera aktualną subdomenę językową
 * @param {Array} parts - części hosta podzielone przez kropki
 * @returns {string|null} - aktualna subdomena lub null jeśli brak
 */
const getCurrentSubdomain = (parts) => {
	return parts.length > 2 ? parts[0] : null;
};

/**
 * Generuje nowy URL z subdomeną językową
 * @param {Object} hostInfo - informacje o hoście
 * @param {string} language - język docelowy
 * @returns {string} - nowy URL
 */
const generateLanguageUrl = (hostInfo, language) => {
	const { location, hostNoPort, parts, port } = hostInfo;

	const domain = parts.length > 2 ? parts.slice(1).join(".") : hostNoPort;

	const newHost = `${language}.${domain}${port}`;

	return `${location.protocol}//${newHost}${location.pathname}${location.search}`;
};

/**
 * Przekierowuje na subdomenę językową jeśli jest to konieczne
 * @param {string} targetLanguage - język docelowy (np. "pl", "en")
 * @returns {boolean} - true jeśli przekierowanie zostało wykonane, false w przeciwnym razie
 */
export const redirectToLanguageSubdomain = (targetLanguage) => {
	try {
		const hostInfo = parseHostInfo();

		if (!hostInfo || hostInfo.isLocal) {
			// Nie przekierowuj na localhost/IP w dev
			return false;
		}

		const currentSubdomain = getCurrentSubdomain(hostInfo.parts);

		// Jeśli brak subdomeny lub jest inna niż targetLanguage -> przekieruj
		if (currentSubdomain !== targetLanguage) {
			const newUrl = generateLanguageUrl(hostInfo, targetLanguage);

			// Używamy assign aby nie psuć historii wstecz dla zmiany języka
			window.location.assign(newUrl);
			return true;
		}

		return false;
	} catch (error) {
		// Ignoruj błędy - nie przerywaj działania aplikacji
		console.warn("Language subdomain redirect failed:", error);
		return false;
	}
};

/**
 * Pobiera język z aktualnej subdomeny
 * @returns {string|null} - język z subdomeny lub null jeśli brak
 */
export const getLanguageFromSubdomain = () => {
	try {
		const hostInfo = parseHostInfo();

		if (!hostInfo || hostInfo.isLocal) {
			return null;
		}

		const subdomain = getCurrentSubdomain(hostInfo.parts);

		// Mapowanie specjalnych subdomen
		if (subdomain === "www") {
			return "pl";
		}

		return subdomain;
	} catch (error) {
		console.warn("Failed to get language from subdomain:", error);
		return null;
	}
};

/**
 * Pobiera subdomenę z aktualnego hosta
 * @returns {string|null} - subdomena lub null jeśli brak
 */
export const getSubdomainFromHost = () => {
	try {
		const hostInfo = parseHostInfo();

		if (!hostInfo || hostInfo.isLocal) {
			return null;
		}

		const subdomain = getCurrentSubdomain(hostInfo.parts);

		return subdomain;
	} catch (error) {
		console.warn("Failed to get language from subdomain:", error);
		return null;
	}
};

/**
 * Generuje URL do subdomeny językowej dla linków SEO
 * @param {string} targetLanguage - język docelowy (np. "pl", "cs", "en")
 * @param {string} pathname - ścieżka URL (np. "/users", "/blog/post")
 * @param {string} search - query string (opcjonalnie)
 * @returns {string} - pełny URL z odpowiednią subdomeną
 */
export const generateLanguageSubdomainUrl = (
	targetLanguage,
	pathname = "",
	search = "",
) => {
	try {
		const hostInfo = parseHostInfo();

		// W przypadku braku hostInfo (SSR) lub środowiska lokalnego, użyj zmiennych środowiskowych
		if (!hostInfo) {
			// Pobierz protokół z zmiennych środowis kowych
			const protocol = (
				process.env.PROTOCOL ||
				process.env.RAZZLE_PROTOCOL ||
				"https"
			).toLowerCase();

			// Pobierz domenę z konfiguracji i usuń wiodącą kropkę jeśli istnieje
			let baseDomain = process.env.RAZZLE_COOKIE_DOMAIN || "datezone.com";

			// Usuń wiodącą kropkę (cookie domain format: .datezone.com -> datezone.com)
			baseDomain = baseDomain.startsWith(".")
				? baseDomain.slice(1)
				: baseDomain;

			const siteDomain = process.env.RAZZLE_PORT
				? `${baseDomain}:${process.env.RAZZLE_PORT}`
				: baseDomain;

			// Dla języka polskiego używamy www.datezone.com
			// Dla pozostałych języków używamy [lang].datezone.com
			const subdomain = targetLanguage === "pl" ? "www" : targetLanguage;

			const newUrl = `${protocol}://${subdomain}.${siteDomain}${pathname}${search}`;

			return newUrl;
		}

		// W środowisku lokalnym zwróć tylko pathname z query params
		if (hostInfo.isLocal) {
			return `${pathname}${search}`;
		}

		const { hostNoPort, parts, port } = hostInfo;

		// Pobierz domenę główną (bez subdomeny)
		const domain = parts.length > 2 ? parts.slice(1).join(".") : hostNoPort;

		// Dla języka polskiego używamy www.datezone.com
		// Dla pozostałych języków używamy [lang].datezone.com
		let newHost;
		if (targetLanguage === "pl") {
			newHost = `www.${domain}${port}`;
		} else {
			newHost = `${targetLanguage}.${domain}${port}`;
		}

		const newUrl = `${hostInfo.location.protocol}//${newHost}${pathname}${search}`;

		return newUrl;
	} catch (error) {
		console.warn("Failed to generate language subdomain URL:", error);
		// W przypadku błędu zwróć oryginalny pathname
		return `${pathname}${search}`;
	}
};
